import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import {
  BarChart3, PieChart, TrendingUp, Users, Loader2,
  Download, Calendar, RefreshCw, AlertTriangle, Clock
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart as RPieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid
} from 'recharts';

const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s || '—'; } };
const IC = "bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A44C] transition-all";

const DONA_COLORS = ['#C9A44C','#E0C878','#997A00','#B8860B','#886B2A','#3B82F6','#22C55E','#EF4444'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-[rgba(201,164,76,0.2)] rounded-lg px-3 py-2 text-xs">
      {label && <div className="text-[#C9A44C] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#F8F6F0' }}>{p.name}: {typeof p.value === 'number' && p.value > 1000 ? fmt(p.value) : p.value}</div>
      ))}
    </div>
  );
};

function SeccionCard({ title, icon: Icon, children, accion }) {
  return (
    <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#C9A44C]" />
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C]">{title}</h3>
        </div>
        {accion}
      </div>
      {children}
    </div>
  );
}

const hoy = new Date().toISOString().slice(0, 10);
const primerDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function Reportes() {
  const [tab, setTab] = useState('facturacion');
  const [desde, setDesde] = useState(primerDiaMes);
  const [hasta, setHasta] = useState(hoy);

  // Facturación
  const [repFact, setRepFact] = useState(null);
  // Casos
  const [repCasos, setRepCasos] = useState(null);
  // Pagos
  const [repPagos, setRepPagos] = useState(null);
  // Vencimientos
  const [repVenc, setRepVenc] = useState(null);
  // Morosos
  const [repMorosos, setRepMorosos] = useState(null);
  // Productividad
  const [prodAbogados, setProdAbogados] = useState([]);
  // Dashboard extras
  const [mensual, setMensual] = useState([]);

  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [f, c, p, v, m, prod, mens] = await Promise.allSettled([
        api.get(`/api/reportes/facturacion?fecha_desde=${desde}&fecha_hasta=${hasta}`),
        api.get(`/api/reportes/casos?fecha_desde=${desde}&fecha_hasta=${hasta}`),
        api.get(`/api/reportes/pagos?fecha_desde=${desde}&fecha_hasta=${hasta}`),
        api.get('/api/reportes/vencimientos'),
        api.get('/api/reportes/morosos'),
        api.get('/api/reportes/dashboard/productividad-abogados'),
        api.get('/api/reportes/dashboard/facturacion-mensual?meses=6'),
      ]);
      if (f.status === 'fulfilled') setRepFact(f.value.data);
      if (c.status === 'fulfilled') setRepCasos(c.value.data);
      if (p.status === 'fulfilled') setRepPagos(p.value.data);
      if (v.status === 'fulfilled') setRepVenc(v.value.data);
      if (m.status === 'fulfilled') setRepMorosos(m.value.data);
      if (prod.status === 'fulfilled') setProdAbogados(prod.value.data.data || []);
      if (mens.status === 'fulfilled') setMensual(mens.value.data.data || []);
    } finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const TABS = [
    { id: 'facturacion', label: 'Facturación', icon: TrendingUp },
    { id: 'casos', label: 'Causas', icon: BarChart3 },
    { id: 'vencimientos', label: 'Vencimientos', icon: AlertTriangle },
    { id: 'morosos', label: 'Morosos', icon: Clock },
    { id: 'productividad', label: 'Productividad', icon: Users },
  ];

  return (
    <DashboardLayout title="Reportes">
      {/* Filtros de fecha */}
      <div className="flex flex-wrap items-center gap-3 mb-5 p-4 bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl">
        <Calendar className="w-4 h-4 text-[#C9A44C]" />
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-[#888] uppercase tracking-wide">Desde</label>
          <input type="date" className={IC} value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-[#888] uppercase tracking-wide">Hasta</label>
          <input type="date" className={IC} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <button onClick={cargar} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-[rgba(201,164,76,0.25)] text-[#C9A44C] hover:bg-[#C9A44C]/10 disabled:opacity-50 transition-all">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${
              tab === id
                ? 'bg-[#C9A44C] text-[#070707] font-semibold'
                : 'bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] text-[#888] hover:text-[#F8F6F0]'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : (
        <div className="space-y-5">

          {/* ── Tab Facturación ─────────────────────────────── */}
          {tab === 'facturacion' && repFact && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total facturado', val: fmt(repFact.resumen?.total_facturado), col: '' },
                  { label: 'Cobrado', val: fmt(repFact.resumen?.total_cobrado), col: 'text-green-300' },
                  { label: 'Pendiente', val: fmt(repFact.resumen?.total_pendiente), col: 'text-red-300' },
                  { label: 'N° honorarios', val: repFact.resumen?.cantidad_honorarios || 0, col: 'text-[#C9A44C]' },
                ].map(({ label, val, col }) => (
                  <div key={label} className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                    <div className={`text-xl font-light mb-0.5 ${col}`} style={{ fontFamily: 'Playfair Display, serif' }}>{val}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#888]">{label}</div>
                  </div>
                ))}
              </div>

              {/* Gráfico línea: facturación mensual */}
              {mensual.length > 0 && (
                <SeccionCard title="Evolución mensual (6 meses)" icon={TrendingUp}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={mensual.map((m) => ({ mes: m.mes?.slice(5), facturado: m.facturado, cobrado: m.cobrado }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#aaa' }}>{v}</span>} />
                      <Line type="monotone" dataKey="facturado" name="Facturado" stroke="#C9A44C" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="cobrado" name="Cobrado" stroke="#22C55E" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </SeccionCard>
              )}

              {/* Por estado */}
              {repFact.por_estado?.length > 0 && (
                <SeccionCard title="Honorarios por estado" icon={PieChart}>
                  <div className="grid md:grid-cols-2 gap-4 items-center">
                    <ResponsiveContainer width="100%" height={200}>
                      <RPieChart>
                        <Pie data={repFact.por_estado.map((e, i) => ({ name: e.estado, value: Number(e.cantidad), color: DONA_COLORS[i % DONA_COLORS.length] }))}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                          {repFact.por_estado.map((_, i) => <Cell key={i} fill={DONA_COLORS[i % DONA_COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#aaa' }}>{v}</span>} />
                      </RPieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {repFact.por_estado.map((e, i) => (
                        <div key={i} className="flex justify-between text-xs p-2 rounded-lg bg-white/[0.02]">
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DONA_COLORS[i % DONA_COLORS.length] }} />
                            {e.estado}
                          </span>
                          <span className="text-[#C9A44C]">{e.cantidad} · {fmt(e.monto_total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </SeccionCard>
              )}
            </>
          )}

          {/* ── Tab Causas ──────────────────────────────────── */}
          {tab === 'casos' && repCasos && (
            <>
              <div className="grid lg:grid-cols-2 gap-5">
                {/* Por estado procesal */}
                <SeccionCard title="Causas por estado procesal" icon={BarChart3}>
                  {repCasos.por_estado?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={repCasos.por_estado.map((e) => ({ name: (e.estado_procesal || 'Sin estado').replace('_', ' '), value: Number(e.cantidad) }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" name="Causas" fill="#C9A44C" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#555] py-8 text-center">Sin datos en el período</p>}
                </SeccionCard>

                {/* Por fuero */}
                <SeccionCard title="Causas por fuero" icon={PieChart}>
                  {repCasos.por_fuero?.length > 0 ? (
                    <div className="space-y-2">
                      {repCasos.por_fuero.slice(0, 8).map((f, i) => {
                        const max = repCasos.por_fuero[0]?.cantidad || 1;
                        const pct = (f.cantidad / max) * 100;
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-[#ccc] truncate">{f.fuero || 'Sin fuero'}</span>
                              <span className="text-[#C9A44C] ml-2">{f.cantidad}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #C9A44C, #E0C878)' }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : <p className="text-sm text-[#555] py-8 text-center">Sin datos en el período</p>}
                </SeccionCard>
              </div>

              {/* Por abogado */}
              {repCasos.por_abogado?.length > 0 && (
                <SeccionCard title="Causas por abogado" icon={Users}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={repCasos.por_abogado.map((a) => ({ name: (a.abogado || '').split(' ')[0], casos: Number(a.casos) }))}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="casos" name="Causas" fill="#C9A44C" radius={[4, 4, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </SeccionCard>
              )}
            </>
          )}

          {/* ── Tab Vencimientos ────────────────────────────── */}
          {tab === 'vencimientos' && repVenc && (
            <div className="space-y-4">
              {[
                { key: 'vencidos', label: 'Vencidos', color: 'text-red-300', borderColor: 'border-red-500/20' },
                { key: 'proximos_7_dias', label: 'Próximos 7 días', color: 'text-[#C9A44C]', borderColor: 'border-[#C9A44C]/20' },
                { key: 'proximos_30_dias', label: 'Próximos 30 días', color: 'text-blue-300', borderColor: 'border-blue-500/20' },
              ].map(({ key, label, color, borderColor }) => (
                <div key={key} className={`bg-[#0E0E0E] border ${borderColor} rounded-xl overflow-hidden`}>
                  <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                    <span className={`text-xs uppercase tracking-[0.15em] ${color}`}>{label}</span>
                    <span className={`text-xs ${color}`}>{repVenc[key]?.length || 0} honorarios</span>
                  </div>
                  {!repVenc[key] || repVenc[key].length === 0 ? (
                    <p className="text-sm text-[#555] py-6 text-center">Sin registros</p>
                  ) : (
                    <div className="divide-y divide-white/[0.04]">
                      {repVenc[key].map((h) => (
                        <div key={h.id} className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-all">
                          <div className="min-w-0">
                            <div className="text-sm truncate">{h.cliente_nombre}</div>
                            <div className="text-[11px] text-[#888]">Vencimiento: {fmtF(h.fecha_vencimiento)}</div>
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            <div className="text-sm">{fmt((h.monto || 0) - (h.monto_pagado || 0))}</div>
                            <div className="text-[10px] text-[#888]">{h.estado}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Tab Morosos ─────────────────────────────────── */}
          {tab === 'morosos' && repMorosos && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                  <div className="text-xl font-light text-red-300 mb-0.5" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {repMorosos.total_morosos || 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#888]">Clientes morosos</div>
                </div>
                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                  <div className="text-xl font-light text-red-300 mb-0.5" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {fmt(repMorosos.total_adeudado_general)}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#888]">Total adeudado</div>
                </div>
              </div>
              {!repMorosos.morosos || repMorosos.morosos.length === 0 ? (
                <div className="text-center py-12 text-[#666] bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl">
                  Sin clientes morosos
                </div>
              ) : (
                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
                  <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 px-5 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-[0.15em] text-[#666]">
                    <span>Cliente</span><span>Honorarios</span><span>Primer venc.</span><span>Total adeudado</span>
                  </div>
                  {repMorosos.morosos.map((m) => (
                    <div key={m.id} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr] gap-2 sm:gap-4 px-5 py-3 border-b border-white/[0.04] hover:bg-white/[0.02] transition-all">
                      <div>
                        <div className="text-sm">{m.cliente_nombre}</div>
                        <div className="text-[11px] text-[#888]">{m.email}</div>
                      </div>
                      <div className="text-sm hidden sm:block">{m.cantidad_honorarios}</div>
                      <div className="text-sm text-red-300 hidden sm:block">{fmtF(m.primer_vencimiento)}</div>
                      <div className="text-sm text-red-300">{fmt(m.total_adeudado)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Tab Productividad ───────────────────────────── */}
          {tab === 'productividad' && (
            <>
              {prodAbogados.length === 0 ? (
                <div className="text-center py-12 text-[#666] bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl">Sin datos</div>
              ) : (
                <>
                  <SeccionCard title="Producción por abogado" icon={Users}>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={prodAbogados.slice(0, 10).map((a) => ({
                        name: (a.abogado || '').split(' ')[0],
                        total: a.total_casos || 0,
                        mes: a.casos_nuevos_mes || 0,
                        movs: a.movimientos_mes || 0,
                      }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#aaa' }}>{v}</span>} />
                        <Bar dataKey="total" name="Total causas" fill="#C9A44C" radius={[4, 4, 0, 0]} barSize={22} />
                        <Bar dataKey="mes" name="Causas este mes" fill="#665020" radius={[4, 4, 0, 0]} barSize={22} />
                        <Bar dataKey="movs" name="Movimientos mes" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SeccionCard>

                  <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
                    <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-[0.15em] text-[#666]">
                      <span>Abogado</span><span>Rol</span><span>Total causas</span><span>En proceso</span><span>Este mes</span>
                    </div>
                    {prodAbogados.map((a) => (
                      <div key={a.id} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 sm:gap-4 px-5 py-3 border-b border-white/[0.04] hover:bg-white/[0.02] transition-all">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-[#C9A44C]/12 border border-[#C9A44C]/25 flex items-center justify-center text-[#C9A44C] text-xs flex-shrink-0">
                            {(a.abogado || '?').charAt(0)}
                          </div>
                          <span className="text-sm">{a.abogado}</span>
                        </div>
                        <span className="text-xs text-[#888] hidden sm:block">{a.rol}</span>
                        <span className="text-sm">{a.total_casos}</span>
                        <span className="text-sm text-[#C9A44C]">{a.casos_en_proceso}</span>
                        <span className="text-sm text-green-300">+{a.casos_nuevos_mes}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
